import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatSelect } from '@angular/material/select';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeWhile, tap } from 'rxjs/operators';
import { AppState } from '../../../../../core/reducers';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { Template } from '../../models';
import { RequestTemplatePage, UpdateTemplateStatusOnServer } from '../../actions';
import { TemplateDatasource } from '../../data-sources';
import { selectTemplateActionLoading, selectTemplateError } from '../../selectors';
import { CreateTemplateComponent } from '../create-template/create-template.component';
import { currentUser } from '../../../../../core/auth';

@Component({
  selector: 'kt-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss'],
})
export class TemplateListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: TemplateDatasource;
  displayedCols = ['id', 'name', 'createdBySuperAdmin', 'isActive', 'actions'];
  searchBarCols = ['searchId', 'searchName', 'searchOrigin', 'searchIsActive', 'searchActions'];
  showSearchBar = false;
  isActiveTracker = 0;
  isSuperAdmin: boolean;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  @ViewChild('searchOriginSelect') searchOriginSelect: MatSelect;
  @ViewChild('searchIsActiveSelect') searchIsActiveSelect: MatSelect;
  originOptions = [
    { id: 1, label: 'All', value: '' },
    { id: 2, label: 'Global', value: 'true' },
    { id: 3, label: 'Company', value: 'false' },
  ];
  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private permissionsService: NgxPermissionsService,
  ) {}

  ngOnInit(): void {
    this.store.select(currentUser).subscribe((user) => {
      this.isSuperAdmin = user.userType === 'Super Admin';
      if (this.isSuperAdmin) {
        this.displayedCols = ['id', 'name', 'isActive', 'actions'];
        this.searchBarCols = ['searchId', 'searchName', 'searchIsActive', 'searchActions'];
      }
    });
    this.dataSource = new TemplateDatasource(this.store, this.paginator);

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadTemplateList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadTemplateList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadTemplateList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFilterConfig() {
    const filter: any = {};

    if (this.searchIsActiveSelect?.value === undefined) this.searchIsActiveSelect.value = 'true';

    filter.isActive = this.searchIsActiveSelect?.value;

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.origin = this.searchOriginSelect?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadTemplateList(): void {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestTemplatePage({ query }));
  }

  formatName(name: string): string {
    return name?.replace(/\.[^/.]+$/, '') || '';
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectTemplateActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectTemplateError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadTemplateList(), 500);
          });
        }),
      )
      .subscribe();
  }

  addTemplate(event: Event): void {
    event.stopPropagation();

    this.updateTemplate(null, new Template());
  }

  updateTemplate(event: Event, template: Template): void {
    event?.stopPropagation();

    const message = this.translate.instant(
      `FORMS_TEMPLATES_MODULE.TEMPLATE.${template.id ? 'UPDATE_MESSAGE' : 'CREATE_MESSAGE'}`,
    );
    const messageType = template.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { template, viewMode: false };

    const dialogRef = this.dialog.open(CreateTemplateComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  toggleTemplateStatus(event: Event, template: Template): void {
    event.stopPropagation();

    const title = this.translate.instant('FORMS_TEMPLATES_MODULE.TEMPLATE.UPDATE_STATUS.TITLE');
    const description = `
      ${
        template.is_active && template.has_mappings
          ? `<div class="font-size-lg font-weight-boldest mb-10">
        <span class="text-danger">WARNING:</span>
        <span>This template fields are already mapped to a form fields.</span>
      </div>`
          : ''
      }
      <div>
        <span class="font-weight-bolder">Are you sure you want to change the status of this template?</span>
        ${
          template.is_active && template.has_mappings
            ? '<span>By hitting Confirm, the form-template mapping will be lost.</span>'
            : ''
        }
      </div>
    `;
    const waitDescription = this.translate.instant(
      'FORMS_TEMPLATES_MODULE.TEMPLATE.UPDATE_STATUS.WAIT_DESCRIPTION',
    );
    const message = this.translate.instant('FORMS_TEMPLATES_MODULE.TEMPLATE.UPDATE_STATUS.MESSAGE');
    const messageType = MessageType.Update;
    const dialogRef = this.layoutUtils.confirmAction(title, description, waitDescription);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        template = { ...template, is_active: !template.is_active };

        this.store.dispatch(new UpdateTemplateStatusOnServer({ template }));
        this.displayMessage(message, messageType);
      });
  }

  canViewTemplate(): boolean {
    return !!this.permissionsService.getPermission('canViewDocumentTemplate');
  }

  viewTemplate(event: Event, template: Template): void {
    event.stopPropagation();

    if (!this.canViewTemplate()) return;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { template, viewMode: true };

    this.dialog.open(CreateTemplateComponent, dialogConfig);
  }

  reloadTemplateList(): void {
    this.paginator.pageIndex = 0;

    this.loadTemplateList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    if (this.searchOriginSelect) this.searchOriginSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadTemplateList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadTemplateList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadTemplateList();
  }

  getStatusClass(status: string): string {
    return status === 'true' ? 'success' : status === 'false' ? 'danger' : 'warning';
  }

  getStatusLabel(status: string): string {
    const translateLabel = `GLOBAL.${
      status === 'true' ? 'ACTIVE' : status === 'false' ? 'INACTIVE' : 'ALL'
    }`;

    return this.translate.instant(translateLabel);
  }

  toggleIsActive(event: Event): void {
    event.stopPropagation();

    this.isActiveTracker = (this.isActiveTracker + 1) % 3;

    switch (this.isActiveTracker) {
      case 0:
        this.searchIsActiveSelect.value = 'true';
        break;
      case 1:
        this.searchIsActiveSelect.value = 'false';
        break;
      case 2:
        this.searchIsActiveSelect.value = '';
        break;
    }

    this.reloadTemplateList();
  }

  handleIsActiveChange(): void {
    switch (this.searchIsActiveSelect.value) {
      case 'true':
        this.isActiveTracker = 0;
        break;
      case 'false':
        this.isActiveTracker = 1;
        break;
      case '':
        this.isActiveTracker = 2;
        break;
    }
  }
}
